import React, { useState, useMemo, useCallback } from 'react'
import { Plus, Search, Filter, User, Phone, Clock, Calendar, IndianRupee, Edit, Trash2 } from 'lucide-react'
import { useData } from '../context/DataContext'
import DriverForm from '../components/DriverForm'
import Modal from '../components/Modal'
import DeleteConfirmModal from '../components/DeleteConfirmModal'

function Drivers() {
  const { state, addDriver, updateDriver, deleteDriver, refreshData } = useData()
  const { drivers, loading, error } = state

  const [showForm, setShowForm] = useState(false)
  const [editingDriver, setEditingDriver] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [selectedDriver, setSelectedDriver] = useState('all')
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, driver: null })



  const handleDeleteDriver = useCallback(async (driverId) => {
    try {
      const result = await deleteDriver(driverId)
      if (!result.success) {
        alert(`Error deleting driver: ${result.error}`)
      }
    } catch (error) {
      console.error('Error deleting driver:', error)
      alert('Error deleting driver. Please try again.')
    }
  }, [deleteDriver])

  const openDeleteModal = useCallback((driver) => {
    setDeleteModal({ isOpen: true, driver })
  }, [])

  const closeDeleteModal = useCallback(() => {
    setDeleteModal({ isOpen: false, driver: null })
  }, [])

  const confirmDelete = useCallback(() => {
    if (deleteModal.driver) {
      handleDeleteDriver(deleteModal.driver.id)
    }
  }, [deleteModal.driver, handleDeleteDriver])

  const handleEditDriver = useCallback((driver) => {
    setEditingDriver(driver)
    setShowForm(true)
  }, [])

  const handleCancelEdit = useCallback(() => {
    setShowForm(false)
    setEditingDriver(null)
  }, [])

  const handleSubmit = useCallback(async (driverData) => {
    try {
      // If editing an existing driver, update it
      if (editingDriver) {
        const result = await updateDriver(editingDriver.id, driverData)
        if (result.success) {
          setShowForm(false)
          setEditingDriver(null)
          return { success: true, data: result.data }
        } else {
          alert(`Error updating driver: ${result.error}`)
          return { success: false, error: result.error }
        }
      } else {
        // Create new driver record with JSONB weeks_data
        const result = await addDriver(driverData)
        if (result.success) {
          // Close the form after successful creation
          setShowForm(false)
          return { success: true, data: result.data }
        } else {
          alert(`Error creating driver: ${result.error}`)
          return { success: false, error: result.error }
        }
      }
    } catch (error) {
      console.error('Error submitting driver:', error)
      alert('An error occurred while saving the driver. Please try again.')
      return { success: false, error: error.message }
    }
  }, [editingDriver, addDriver, updateDriver])

  // Show loading state during operations
  const isUpdating = loading && editingDriver
  const isCreating = loading && !editingDriver && showForm

  // Memoized filtered drivers for better performance
  const filteredDrivers = useMemo(() => {
    return drivers.filter(driver => {
      const matchesSearch = driver.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           driver.phone_number?.includes(searchTerm)
      const matchesFilter = filterStatus === 'all' || driver.status === filterStatus
      const matchesDriver = selectedDriver === 'all' || driver.id === selectedDriver
      
      // Date filtering - check if any work dates in weeks_data fall within the range
      let matchesDate = true
      if (fromDate || toDate) {
        const weeksData = driver.weeks_data || []
        let hasMatchingDate = false
        
        // Check each week's work days for matching dates
        weeksData.forEach(week => {
          if (week.work_days) {
            week.work_days.forEach(day => {
              if (day.work_date) {
                // Parse dates properly - work_date should already be in YYYY-MM-DD format
                const workDate = new Date(day.work_date)
                const from = fromDate ? new Date(fromDate) : null
                const to = toDate ? new Date(toDate) : null
                
                // Set time to start/end of day for proper comparison
                if (from) from.setHours(0, 0, 0, 0)
                if (to) to.setHours(23, 59, 59, 999)
                workDate.setHours(0, 0, 0, 0)
                
                let dateMatches = true
                if (from && to) {
                  dateMatches = workDate >= from && workDate <= to
                } else if (from) {
                  dateMatches = workDate >= from
                } else if (to) {
                  dateMatches = workDate <= to
                }
                
                if (dateMatches) {
                  hasMatchingDate = true
                }
              }
            })
          }
        })
        
        // If we have date filters but no matching dates found, exclude this driver
        if ((fromDate || toDate) && !hasMatchingDate) {
          matchesDate = false
        }
        
        // If driver has no weeks_data at all and we have date filters, exclude them
        if ((fromDate || toDate) && (!weeksData || weeksData.length === 0)) {
          matchesDate = false
        }
      }
      
      return matchesSearch && matchesFilter && matchesDriver && matchesDate
    })
  }, [drivers, searchTerm, filterStatus, selectedDriver, fromDate, toDate])

  const totalDrivers = drivers.length
  const activeDrivers = drivers.filter(d => d.status === 'active').length
  
  // Filtered stats - calculate from JSONB weeks_data
  const filteredTotalDrivers = filteredDrivers.length
  const filteredActiveDrivers = filteredDrivers.filter(d => d.status === 'active').length
  
  // Helper function to check if a work day falls within the date range
  const isWorkDayInRange = (workDate) => {
    if (!fromDate && !toDate) return true
    
    const dayDate = new Date(workDate)
    const from = fromDate ? new Date(fromDate) : null
    const to = toDate ? new Date(toDate) : null
    
    // Set time to start/end of day for proper comparison
    if (from) from.setHours(0, 0, 0, 0)
    if (to) to.setHours(23, 59, 59, 999)
    dayDate.setHours(0, 0, 0, 0)
    
    if (from && to) {
      return dayDate >= from && dayDate <= to
    } else if (from) {
      return dayDate >= from
    } else if (to) {
      return dayDate <= to
    }
    return true
  }

  // Calculate totals from weeks_data JSONB structure - only for work days within date range
  const filteredTotalHours = filteredDrivers.reduce((total, driver) => {
    const weeksData = driver.weeks_data || []
    let driverTotalHours = 0
    weeksData.forEach(week => {
      if (week.work_days) {
        week.work_days.forEach(day => {
          if (isWorkDayInRange(day.work_date)) {
            driverTotalHours += parseFloat(day.total_hours || 0)
          }
        })
      }
    })
    return total + driverTotalHours
  }, 0)
  
  const filteredTotalSalary = filteredDrivers.reduce((total, driver) => {
    const weeksData = driver.weeks_data || []
    let driverTotalSalary = 0
    weeksData.forEach(week => {
      if (week.work_days) {
        week.work_days.forEach(day => {
          if (isWorkDayInRange(day.work_date)) {
            driverTotalSalary += parseFloat(day.day_amount || 0)
          }
        })
      }
    })
    return total + driverTotalSalary
  }, 0)
  
  const filteredTotalAdvance = filteredDrivers.reduce((total, driver) => total + parseFloat(driver.advance_amount || 0), 0)
  
  const filteredTotalAdvanceDeduction = filteredDrivers.reduce((total, driver) => {
    const weeksData = driver.weeks_data || []
    let totalDeduction = 0
    weeksData.forEach(week => {
      // Only include advance deduction if any work day in this week falls within the date range
      const hasWorkDayInRange = week.work_days && week.work_days.some(day => isWorkDayInRange(day.work_date))
      if (hasWorkDayInRange) {
        totalDeduction += parseFloat(week.advance_deduction || 0)
      }
    })
    return total + totalDeduction
  }, 0)
  
  const filteredRemainingAdvance = filteredTotalAdvance - filteredTotalAdvanceDeduction

  // Helper function to safely format numbers
  const safeToFixed = (value, decimals = 2) => {
    const num = parseFloat(value) || 0
    return num.toFixed(decimals)
  }

  return (
    <div className="container mx-auto px-4 py-2">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Drivers Management</h1>
          <p className="text-gray-600">Manage driver information and work schedules</p>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-3">
        <button
          onClick={() => setShowForm(true)}
            className="btn btn-primary flex items-center space-x-2"
        >
            <Plus className="h-5 w-5" />
            <span>Add Driver</span>
        </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4">
        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <User className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-sm font-medium text-gray-600">Total Drivers</p>
              <p className="text-base sm:text-lg font-semibold text-gray-900">{filteredTotalDrivers}</p>
              {filteredTotalDrivers !== totalDrivers && (
                <p className="text-xs text-gray-500">of {totalDrivers} total</p>
              )}
            </div>
          </div>
        </div>



        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-sm font-medium text-gray-600">Total Hours</p>
              <p className="text-base sm:text-lg font-semibold text-gray-900">
                {safeToFixed(filteredTotalHours, 1)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <IndianRupee className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-sm font-medium text-gray-600">Total Salary</p>
              <p className="text-base sm:text-lg font-semibold text-gray-900">
                ₹{safeToFixed(filteredTotalSalary)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <IndianRupee className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600" />
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-sm font-medium text-gray-600">Total Advance</p>
              <p className="text-base sm:text-lg font-semibold text-gray-900">
                ₹{safeToFixed(filteredTotalAdvance)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <IndianRupee className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-sm font-medium text-gray-600">Remaining Advance</p>
              <p className="text-base sm:text-lg font-semibold text-gray-900">
                ₹{safeToFixed(filteredRemainingAdvance)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200 my-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or phone number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="filter-input pl-8 sm:pl-10 text-xs sm:text-sm"
              />
            </div>
          </div>
          


          {/* Driver Filter */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Driver</label>
            <select
              value={selectedDriver}
              onChange={(e) => setSelectedDriver(e.target.value)}
              className="filter-input text-gray-900 text-xs sm:text-sm"
            >
              <option value="all" className="text-gray-900">All Drivers</option>
              {drivers.map(driver => (
                <option key={driver.id} value={driver.id} className="text-gray-900">
                  {driver.name}
                </option>
              ))}
            </select>
          </div>

          {/* From Date */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                const selectedDate = e.target.value
                setFromDate(selectedDate)
                // If toDate is before the new fromDate, clear toDate
                if (toDate && selectedDate && toDate < selectedDate) {
                  setToDate('')
                }
              }}
              className="filter-input text-gray-900 text-xs sm:text-sm"
              max={toDate || undefined}
            />
          </div>

          {/* To Date */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                const selectedDate = e.target.value
                setToDate(selectedDate)
                // If fromDate is after the new toDate, clear fromDate
                if (fromDate && selectedDate && fromDate > selectedDate) {
                  setFromDate('')
                }
              }}
              className="filter-input text-gray-900 text-xs sm:text-sm"
              min={fromDate || undefined}
            />
          </div>
        </div>
        
        {/* Clear Filters Button */}
        <div className="mt-3 sm:mt-4 flex justify-end">
          <button
            onClick={() => {
              setSearchTerm('')
              setSelectedDriver('all')
              setFromDate('')
              setToDate('')
            }}
            className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm bg-gradient-to-r from-brand-orange to-brand-red text-white rounded-lg hover:from-orange-500 hover:to-red-600 transition-all duration-200 font-medium"
          >
            Clear All Filters
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-red mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading drivers...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">Error: {error}</p>
        </div>
      )}

      {/* Drivers List */}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDrivers.length > 0 ? (
            filteredDrivers.map(driver => (
              <div key={driver.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                {/* Driver Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {driver.name}
                    </h3>
                    <div className="flex items-center text-sm text-gray-600 mb-2">
                      <Phone className="h-4 w-4 mr-2 text-gray-400" />
                      <span>{driver.phone_number}</span>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditDriver(driver)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                      title="Edit driver"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    {/* Delete button hidden */}
                    {/* <button
                      onClick={() => openDeleteModal(driver)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                      title="Delete driver"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button> */}
                  </div>
                </div>

                {/* Basic Work Information */}
                <div className="space-y-2 mb-4">
                  {driver.weeks_data && driver.weeks_data.length > 0 && (
                    <div className="text-sm text-gray-600">
                      <span>Work Schedule: {driver.weeks_data.length} week(s)</span>
                    </div>
                  )}
                </div>

                {/* Financial Summary */}
                <div className="space-y-2 mb-4">
                  {driver.weeks_data && driver.weeks_data.length > 0 && (
                    <>
                      <div className="flex items-center text-sm text-gray-600">
                        <Clock className="h-4 w-4 mr-2 text-gray-400" />
                        <span>Total Hours: {(() => {
                          let total = 0
                          driver.weeks_data.forEach(week => {
                            week.work_days.forEach(day => {
                              if (isWorkDayInRange(day.work_date)) {
                                total += parseFloat(day.total_hours || 0)
                              }
                            })
                          })
                          return total
                        })()} hrs</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <IndianRupee className="h-4 w-4 mr-2 text-gray-400" />
                        <span>Total Salary: ₹{(() => {
                          let totalSalary = 0
                          driver.weeks_data.forEach(week => {
                            week.work_days.forEach(day => {
                              if (isWorkDayInRange(day.work_date)) {
                                totalSalary += parseFloat(day.day_amount || 0)
                              }
                            })
                          })
                          return safeToFixed(totalSalary)
                        })()}</span>
                      </div>
                    </>
                  )}
                  
                  {driver.advance_amount > 0 && (
                    <div className="flex items-center text-sm text-gray-600">
                      <IndianRupee className="h-4 w-4 mr-2 text-gray-400" />
                      <span>Advance: ₹{driver.advance_amount}</span>
                    </div>
                  )}
                  
                  {driver.weeks_data && driver.weeks_data.length > 0 && (
                    <div className="flex items-center text-sm text-gray-600">
                      <IndianRupee className="h-4 w-4 mr-2 text-gray-400" />
                      <span>Advance Deductions: ₹{(() => {
                        let total = 0
                        driver.weeks_data.forEach(week => {
                          // Only include advance deduction if any work day in this week falls within the date range
                          const hasWorkDayInRange = week.work_days && week.work_days.some(day => isWorkDayInRange(day.work_date))
                          if (hasWorkDayInRange) {
                            total += parseFloat(week.advance_deduction || 0)
                          }
                        })
                        return safeToFixed(total)
                      })()}</span>
                    </div>
                  )}
                  
                  {driver.weeks_data && driver.weeks_data.length > 0 && (
                    <div className="flex items-center text-sm text-gray-600">
                      <IndianRupee className="h-4 w-4 mr-2 text-gray-400" />
                      <span>Final Salary: ₹{(() => {
                        let totalSalary = 0
                        let totalDeductions = 0
                        driver.weeks_data.forEach(week => {
                          // Only include work days within date range
                          week.work_days.forEach(day => {
                            if (isWorkDayInRange(day.work_date)) {
                              totalSalary += parseFloat(day.day_amount || 0)
                            }
                          })
                          // Only include advance deduction if any work day in this week falls within the date range
                          const hasWorkDayInRange = week.work_days && week.work_days.some(day => isWorkDayInRange(day.work_date))
                          if (hasWorkDayInRange) {
                            totalDeductions += parseFloat(week.advance_deduction || 0)
                          }
                        })
                        return safeToFixed(totalSalary - totalDeductions)
                      })()}</span>
                    </div>
                  )}
                  
                  {driver.advance_amount > 0 && driver.weeks_data && driver.weeks_data.length > 0 && (
                    <div className="flex items-center text-sm text-gray-600">
                      <IndianRupee className="h-4 w-4 mr-2 text-gray-400" />
                      <span>Remaining Advance: ₹{(() => {
                        let totalDeductions = 0
                        driver.weeks_data.forEach(week => {
                          // Only include advance deduction if any work day in this week falls within the date range
                          const hasWorkDayInRange = week.work_days && week.work_days.some(day => isWorkDayInRange(day.work_date))
                          if (hasWorkDayInRange) {
                            totalDeductions += parseFloat(week.advance_deduction || 0)
                          }
                        })
                        return safeToFixed(parseFloat(driver.advance_amount) - totalDeductions)
                      })()}</span>
                    </div>
                  )}
                </div>

                {/* Driver Info */}
                <div className="text-xs text-gray-400 border-t border-gray-100 pt-3">
                  <div>Created: {new Date(driver.created_at).toLocaleDateString()}</div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full">
              <div className="text-center py-12">
                <div className="bg-gray-100 rounded-full p-3 w-16 h-16 mx-auto mb-4">
                  <User className="h-10 w-10 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {(searchTerm || filterStatus !== 'all' || fromDate || toDate) ? 'No drivers found' : 'No drivers yet'}
                </h3>
                <p className="text-gray-500 mb-4">
                  {(searchTerm || filterStatus !== 'all' || fromDate || toDate)
                    ? 'Try adjusting your search or filter criteria'
                    : 'Get started by adding your first driver'
                  }
                </p>
                {!searchTerm && filterStatus === 'all' && !fromDate && !toDate && (
                  <button
                    onClick={() => setShowForm(true)}
                    className="btn btn-primary"
                  >
                    Add Your First Driver
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Driver Form Modal */}
      <Modal 
        isOpen={showForm} 
        onClose={handleCancelEdit}
        title={editingDriver ? "Edit Driver" : "Add New Driver"}
        size="max-w-3xl"
      >
        <DriverForm 
          onSubmit={handleSubmit} 
          onCancel={handleCancelEdit}
          editingDriver={editingDriver}
          isSubmitting={isUpdating || isCreating}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title="Delete Driver"
        message={`Are you sure you want to delete "${deleteModal.driver?.name}"?`}
        itemName={deleteModal.driver?.name || 'Driver'}
      />
    </div>
  )
}

export default Drivers
